import _ from 'lodash';
import {connect} from 'react-redux';

import {getShouldHideTeamTagCounters} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {
  CollapsedItemStates,
  computeIsItemExpanded,
  IsExpandedPredicate,
} from '../../../../helpers/expansionTogglingHelpers';
import {VisualCustomView} from '../../../../helpers/models/customViewHelpers';
import {findEditModeChildTagIdsToRemove, findEditModeParentTags} from '../../../../helpers/models/tagHelpers';
import {ParentTagOverridesMap} from '../../../../helpers/visualCustomViewHelpers';
import {VisualWorkspaceTag} from '../../../../models/api/visualWorkspaceModel';
import {AppState} from '../../../../store/appState';
import {getTags} from '../../../../store/data/tags/tagsSelectors';
import {TagModelMap} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {makeGetVisualWorkspaceTeammateWorkspaceVisualCustomViews} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceSelectors';
import {ToggleTagHandler} from '../editor/context/workspacesEditorContext';
import {ToggleVisualCustomViewHandler} from '../editor/teammateWorkspaceEditorContainer';
import {WorkspaceCustomViews} from '../workspaceCustomViews';

/*
 * Constants.
 */

const defaultExpandedState = true;

/*
 * Props.
 */

export interface TeammateWorkspaceEditorCustomViewsContainerProps {
  visualWorkspaceTags: ReadonlyArray<VisualWorkspaceTag>;
  shouldAlphabetizeWorkspace: boolean;
  collapsedItems: CollapsedItemStates;
  tagModelMap: TagModelMap;
  onToggleTag: ToggleTagHandler;
  parentTagOverridesMap: ParentTagOverridesMap;
}

/*
 * Selectors.
 */

function getVisualWorkspaceTags(state: AppState, props: TeammateWorkspaceEditorCustomViewsContainerProps) {
  return props.visualWorkspaceTags;
}

function getCollapsedItems(state: AppState, props: TeammateWorkspaceEditorCustomViewsContainerProps) {
  return props.collapsedItems;
}

function getTagModelMap(state: AppState, props: TeammateWorkspaceEditorCustomViewsContainerProps) {
  return props.tagModelMap;
}

function getParentTagOverridesMap(state: AppState, props: TeammateWorkspaceEditorCustomViewsContainerProps) {
  return props.parentTagOverridesMap;
}

const getFindIsItemExpanded = createSelector(
  [getCollapsedItems],
  (collapsedItems): IsExpandedPredicate =>
    (key, defaultValue) =>
      computeIsItemExpanded(collapsedItems, key, defaultValue),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<TeammateWorkspaceEditorCustomViewsContainerProps>()(() => ({
  visualCustomViews: makeGetVisualWorkspaceTeammateWorkspaceVisualCustomViews({
    getVisualWorkspaceTags,
    defaultExpandedState,
    getFindIsItemExpanded,
    getOptionalAdditionalTagsMap: getTagModelMap,
    getOptionalParentTagOverridesMap: getParentTagOverridesMap,
    shouldRestrictToVisibleItems: false,
  }),
  tags: getTags,
  shouldHideTeamTagCounters: getShouldHideTeamTagCounters,
}));

export const TeammateWorkspaceEditorCustomViewsContainer = connect(
  mapStateToProps,
  null,
  (stateProps, dispatchProps, ownProps) => {
    const {tags} = stateProps;
    const {tagModelMap, onToggleTag, visualWorkspaceTags, parentTagOverridesMap} = ownProps;

    const onToggleCustomView: ToggleVisualCustomViewHandler = (vcv) => {
      const searchTag = [vcv.tag];
      const combinedTags = _(searchTag)
        .filter((t) => !tags[t.id])
        .keyBy((t) => t.id)
        .assign(tags)
        .merge(tagModelMap)
        .value();

      const tagId = searchTag[0].id;
      const allParentTags = findEditModeParentTags(combinedTags, parentTagOverridesMap, searchTag[0]);
      const visualWorkspaceTagIds = visualWorkspaceTags.map((tag) => tag.id);

      const allChildTagIds = findEditModeChildTagIdsToRemove({
        tagId,
        visibleTagIds: visualWorkspaceTagIds,
        parentTagOverridesMap,
        combinedTags,
      });
      const allChildTags = _(allChildTagIds)
        .map((id) => combinedTags[id])
        .compact()
        .value();

      // Upon toggle, we need to store the parent and child tag objects.
      // This is so that we can avoid conflicting selection states, which are possible when we re-nest tags.
      // e.g. If de-selected tags become the parents of selected tags, these parents need to be auto-selected.
      onToggleTag({tag: searchTag[0], allParentTags, allChildTags, ifUsedHelpTip: false});
    };

    return {
      ...stateProps,
      ...ownProps,
      onCustomViewClick: (vcv: VisualCustomView) => onToggleCustomView(vcv),
      visualWorkspaceUserTags: visualWorkspaceTags,
    };
  },
)(WorkspaceCustomViews);
